import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageChat from "../components/PageChat";
import {
    ArrowLeft, Coins, Lock, Loader2, Trophy, XCircle, Users, Spade,
} from "lucide-react";

const ANTES = [5, 10, 25, 50, 100];

function wsUrl(token, ante) {
    const base = API.replace(/^http/, "ws");
    return `${base}/poker/ws?token=${encodeURIComponent(token)}&ante=${ante}`;
}

const SUIT_CHAR = { s: "\u2660", h: "\u2665", d: "\u2666", c: "\u2663" };
const SUIT_COLOR = { s: "#0A0A0A", c: "#0A0A0A", h: "#E11D48", d: "#E11D48" };

function Card({ card, hidden, small }) {
    const size = small ? "w-10 h-14 text-base" : "w-14 h-20 text-xl sm:w-16 sm:h-24 sm:text-2xl";
    if (!card || hidden || card === "??") {
        return (
            <div className={`${size} bg-vibrantblue border-[3px] border-ink rounded-lg shadow-brutal flex items-center justify-center`}>
                <div className="w-full h-full bg-[repeating-linear-gradient(45deg,#2563EB,#2563EB_4px,#1E40AF_4px,#1E40AF_8px)] rounded-md" />
            </div>
        );
    }
    const r = card[0];
    const s = card[1];
    const rank = r === "T" ? "10" : r;
    return (
        <div
            className={`${size} bg-white border-[3px] border-ink rounded-lg shadow-brutal flex flex-col items-center justify-center font-heading font-black`}
            style={{ color: SUIT_COLOR[s] }}
        >
            <div className="leading-none">{rank}</div>
            <div className="leading-none">{SUIT_CHAR[s]}</div>
        </div>
    );
}

function actionLabel(a) {
    if (!a) return "";
    const m = { fold: "folded", check: "checked", call: "called", bet: "bet", raise: "raised to", all_in: "all-in" };
    const lbl = m[a.action] || a.action;
    return a.amount != null ? `${lbl} ${a.amount}` : lbl;
}

export default function Poker() {
    const { user, loading: authLoading, refresh } = useAuth();
    const [credits, setCredits] = useState(null);
    const [ante, setAnte] = useState(5);
    const [phase, setPhase] = useState("idle"); // idle | queued | playing | ended
    const [match, setMatch] = useState(null);
    const [state, setState] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [actionError, setActionError] = useState("");
    const [queueSizes, setQueueSizes] = useState({});
    const [openTables, setOpenTables] = useState([]);
    const [raiseTo, setRaiseTo] = useState(0);
    const [now, setNow] = useState(Date.now() / 1000);

    const wsRef = useRef(null);

    const isLoggedIn = user && user !== false;

    const loadBalance = useCallback(async () => {
        if (!isLoggedIn) return;
        try { const { data } = await api.get("/payments/i2v/balance"); setCredits(data.credits); } catch { /* ignore */ }
    }, [isLoggedIn]);

    const loadQueues = useCallback(async () => {
        try {
            const [{ data: q }, { data: o }] = await Promise.all([
                api.get("/poker/queues"),
                api.get("/poker/open_tables"),
            ]);
            setQueueSizes(q.queue_sizes || {});
            setOpenTables(o.tables || []);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { loadBalance(); loadQueues(); }, [loadBalance, loadQueues]);
    useEffect(() => {
        const t = setInterval(loadQueues, 4000);
        return () => clearInterval(t);
    }, [loadQueues]);

    // Tick clock for countdown
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now() / 1000), 250);
        return () => clearInterval(t);
    }, []);

    // Sync raiseTo when it's our turn
    useEffect(() => {
        if (!state) return;
        const la = state.legal_actions;
        if (la && la.actor && la.min_raise_to) {
            setRaiseTo((cur) => {
                if (cur < la.min_raise_to || cur > la.max_raise_to) return la.min_raise_to;
                return cur;
            });
        }
    }, [state]);

    const findMatch = () => {
        setError("");
        setActionError("");
        if (!isLoggedIn) return;
        const token = localStorage.getItem("vp_token");
        if (!token) { setError("Please log out and back in"); return; }
        if ((credits ?? 0) < ante) { setError(`Need ${ante} credits to play`); return; }
        const ws = new WebSocket(wsUrl(token, ante));
        wsRef.current = ws;
        setResult(null);
        setMatch(null);
        setState(null);
        setPhase("queued");
        ws.onmessage = (ev) => {
            const msg = JSON.parse(ev.data);
            if (msg.type === "queued") {
                loadBalance();
            } else if (msg.type === "match_found") {
                setMatch(msg);
                setPhase("playing");
            } else if (msg.type === "state") {
                setState(msg.state);
            } else if (msg.type === "action_error") {
                setActionError(msg.message || "Invalid action");
                setTimeout(() => setActionError(""), 2500);
            } else if (msg.type === "match_end") {
                setResult(msg);
                setPhase("ended");
                loadBalance();
            } else if (msg.type === "error") {
                setError(msg.message || "Server error");
                setPhase("idle");
            }
        };
        ws.onclose = () => {
            wsRef.current = null;
            setPhase((p) => (p === "playing" || p === "queued" ? "ended" : p));
            loadBalance();
        };
        ws.onerror = () => setError("Connection failed");
    };

    const cancelQueue = () => {
        const ws = wsRef.current; if (ws) try { ws.close(); } catch { /* ignore */ }
        setPhase("idle"); setMatch(null); setState(null);
        loadBalance();
    };

    const sendAction = (action, amount) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== 1) return;
        ws.send(JSON.stringify({ type: "action", action, amount }));
    };

    const mySeat = state?.you_seat;
    const me = state?.players?.find((p) => p.seat === mySeat);
    const opp = state?.players?.find((p) => p.seat !== mySeat);
    const la = state?.legal_actions || {};
    const timeLeft = state?.action_deadline && la.actor
        ? Math.max(0, state.action_deadline - now)
        : 0;
    const showdown = state?.last_showdown;

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <Link to="/" data-testid="poker-back-link" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                Back to Endfb
            </Link>

            <div className="flex gap-2 mb-4" data-testid="poker-mode-tabs">
                <Link
                    to="/poker"
                    data-testid="poker-tab-headsup"
                    className="brutal-btn bg-ink text-white text-xs py-2 px-3"
                >
                    <Spade className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> Heads-Up
                </Link>
                <Link
                    to="/poker/tournaments"
                    data-testid="poker-tab-tournaments"
                    className="brutal-btn bg-white text-ink text-xs py-2 px-3"
                >
                    <Trophy className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> Tournaments
                </Link>
            </div>

            <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-vibrantorange border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg flex-shrink-0">
                    <Spade className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Heads-up <span className="text-vibrantpink">poker</span>
                    </h1>
                    <p className="font-body text-ink/70 mt-1">
                        2-player Texas Hold&apos;em. 20-chip stacks. Last one with chips wins the pot.
                    </p>
                </div>
                {isLoggedIn && credits != null && (
                    <div className="hidden sm:flex items-center gap-1.5 bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-2 shadow-brutal flex-shrink-0">
                        <Coins className="w-4 h-4 text-ink" strokeWidth={2.5} />
                        <span className="font-heading font-bold text-sm text-ink" data-testid="poker-credits">{credits}</span>
                    </div>
                )}
            </div>

            {authLoading ? (
                <div className="font-heading font-bold text-ink/60">Loading…</div>
            ) : !isLoggedIn ? (
                <div data-testid="poker-login-gate" className="brutal-card p-8 bg-white max-w-lg">
                    <div className="w-12 h-12 bg-vibrantpink border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal mb-4">
                        <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="font-heading font-black text-3xl text-ink tracking-tighter mb-2">Log in to play</h2>
                    <p className="font-body text-ink/70 mb-5">Ante credits, get matched, take the pot.</p>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/login" className="brutal-btn-primary">Log in</Link>
                        <Link to="/register" className="brutal-btn-secondary">Create account</Link>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Table */}
                    <div className="lg:col-span-8">
                        <div className="brutal-card p-4 sm:p-6 bg-[#0E5E3A]" data-testid="poker-table">
                            {/* Opponent */}
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-white">
                                    <div className="font-heading font-bold text-xs uppercase tracking-widest opacity-70">Opponent</div>
                                    <div className="font-heading font-black text-xl" data-testid="poker-opp-name">@{opp?.username || "—"}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-1 shadow-brutal">
                                        <span className="font-heading font-black text-lg text-ink" data-testid="poker-opp-chips">{opp?.chips ?? 0}</span>
                                        <span className="font-body text-xs text-ink/60 ml-1">chips</span>
                                    </div>
                                    {opp?.folded && <span className="font-heading font-bold text-[10px] uppercase text-white bg-vibrantpink border-2 border-ink rounded px-2 py-0.5">Folded</span>}
                                    {opp?.all_in && <span className="font-heading font-bold text-[10px] uppercase text-ink bg-vibrantyellow border-2 border-ink rounded px-2 py-0.5">All-in</span>}
                                </div>
                            </div>
                            <div className="flex gap-2 mb-1">
                                {(opp?.hole || ["??", "??"]).map((c, i) => (
                                    <Card key={i} card={c} hidden={c === "??"} small />
                                ))}
                                {opp?.bet > 0 && (
                                    <div className="ml-2 flex items-center font-heading font-black text-white">
                                        <Coins className="w-4 h-4 mr-1 text-vibrantyellow" strokeWidth={2.5} />
                                        {opp.bet}
                                    </div>
                                )}
                            </div>

                            {/* Community + Pot */}
                            <div className="my-6 bg-[#0a4a2d] border-[3px] border-ink rounded-2xl p-4 shadow-brutal-lg">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="text-white/80 font-heading font-bold text-xs uppercase tracking-widest">
                                        {phase === "playing" ? `Hand #${state?.hand_number || 1} · ${state?.street || "—"}` : "Table"}
                                    </div>
                                    <div className="bg-vibrantorange border-[3px] border-ink rounded-xl px-3 py-1 shadow-brutal">
                                        <span className="font-heading font-black text-white text-xs uppercase tracking-widest mr-1">Pot</span>
                                        <span className="font-heading font-black text-white text-lg" data-testid="poker-pot">{state?.pot ?? 0}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2 justify-center" data-testid="poker-community">
                                    {[0, 1, 2, 3, 4].map((i) => {
                                        const c = state?.community?.[i];
                                        return c
                                            ? <Card key={i} card={c} />
                                            : <div key={i} className="w-14 h-20 sm:w-16 sm:h-24 border-[3px] border-dashed border-white/30 rounded-lg" />;
                                    })}
                                </div>
                                {state?.last_action && (
                                    <div className="text-center mt-3 font-heading font-bold text-xs text-white/80">
                                        {(state.players?.find((p) => p.seat === state.last_action.seat)?.username || "?")} {actionLabel(state.last_action)}
                                    </div>
                                )}
                                {showdown && (
                                    <div className="mt-3 text-center font-heading font-black text-white" data-testid="poker-showdown">
                                        {showdown.by_fold
                                            ? `${state.players?.find((p) => p.seat === showdown.winner)?.username} wins ${showdown.pot} (fold)`
                                            : showdown.winner === -1
                                                ? `Split pot · ${showdown.pot}`
                                                : `${state.players?.find((p) => p.seat === showdown.winner)?.username} wins ${showdown.pot} with ${showdown.ranks?.[showdown.winner]?.name}`}
                                    </div>
                                )}
                            </div>

                            {/* Me */}
                            <div className="flex gap-2 items-end">
                                {(me?.hole || ["??", "??"]).map((c, i) => (
                                    <Card key={i} card={c} hidden={c === "??"} />
                                ))}
                                {me?.bet > 0 && (
                                    <div className="ml-2 flex items-center font-heading font-black text-white">
                                        <Coins className="w-4 h-4 mr-1 text-vibrantyellow" strokeWidth={2.5} />
                                        {me.bet}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center justify-between mt-3">
                                <div className="text-white">
                                    <div className="font-heading font-bold text-xs uppercase tracking-widest opacity-70">You</div>
                                    <div className="font-heading font-black text-xl" data-testid="poker-my-name">@{me?.username || "—"}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-1 shadow-brutal">
                                        <span className="font-heading font-black text-lg text-ink" data-testid="poker-my-chips">{me?.chips ?? 0}</span>
                                        <span className="font-body text-xs text-ink/60 ml-1">chips</span>
                                    </div>
                                    {me?.folded && <span className="font-heading font-bold text-[10px] uppercase text-white bg-vibrantpink border-2 border-ink rounded px-2 py-0.5">Folded</span>}
                                    {me?.all_in && <span className="font-heading font-bold text-[10px] uppercase text-ink bg-vibrantyellow border-2 border-ink rounded px-2 py-0.5">All-in</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Side panel */}
                    <div className="lg:col-span-4 space-y-4">
                        {phase === "idle" && (
                            <div data-testid="poker-lobby" className="brutal-card p-5 bg-vibrantyellow">
                                <div className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-2">Your wager (credits)</div>
                                <input
                                    type="number"
                                    min={1}
                                    max={10000}
                                    step={1}
                                    value={ante}
                                    onChange={(e) => setAnte(Math.max(1, Math.min(10000, parseInt(e.target.value || "0", 10) || 0)))}
                                    data-testid="poker-ante-input"
                                    className="brutal-card w-full py-2 px-3 text-lg font-heading font-black bg-white text-ink mb-2"
                                />
                                <div className="grid grid-cols-5 gap-1.5 mb-4">
                                    {ANTES.map((a) => (
                                        <button
                                            key={a}
                                            type="button"
                                            onClick={() => setAnte(a)}
                                            data-testid={`poker-ante-${a}`}
                                            className={`brutal-btn py-1 text-xs ${ante === a ? "bg-ink text-white" : "bg-white text-ink"}`}
                                        >
                                            {a}
                                        </button>
                                    ))}
                                </div>
                                <div className="brutal-card p-3 bg-white mb-3">
                                    <div className="flex items-center justify-between">
                                        <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Winner takes</span>
                                        <span className="font-heading font-black text-2xl text-ink">{ante * 2} cr</span>
                                    </div>
                                </div>
                                {error && <p data-testid="poker-error" className="font-body font-semibold text-sm text-vibrantpink mb-2">{error}</p>}
                                <button onClick={findMatch} disabled={(credits ?? 0) < ante || ante < 1} className="brutal-btn-primary w-full" data-testid="poker-find-button">
                                    <Spade className="w-4 h-4 mr-2" strokeWidth={2.5} /> Wager {ante} cr
                                </button>
                                {(credits ?? 0) < ante && (
                                    <Link to="/buy-credits" className="brutal-btn-secondary w-full text-sm py-2 mt-2 block text-center">
                                        Buy credits
                                    </Link>
                                )}
                                {openTables.length > 0 && (
                                    <div className="mt-4" data-testid="poker-open-tables">
                                        <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/70 mb-1.5">Open tables (click to match)</div>
                                        <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                            {openTables.map((t) => (
                                                <button
                                                    key={t.ante}
                                                    type="button"
                                                    onClick={() => setAnte(t.ante)}
                                                    data-testid={`poker-open-table-${t.ante}`}
                                                    className="brutal-card w-full flex items-center justify-between px-3 py-2 bg-white hover:bg-cream text-left"
                                                >
                                                    <span className="font-heading font-black text-sm text-ink">{t.ante} cr</span>
                                                    <span className="font-body text-[11px] text-ink/60">
                                                        {t.waiting} waiting · @{t.host_name}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="mt-3 font-body text-[11px] text-ink/60 flex items-center gap-1.5">
                                    <Users className="w-3.5 h-3.5" strokeWidth={2.5} />
                                    ante {ante}: {queueSizes[ante] || 0} waiting
                                </div>
                            </div>
                        )}

                        {phase === "queued" && (
                            <div data-testid="poker-queued" className="brutal-card p-6 bg-white text-center">
                                <Loader2 className="w-12 h-12 mx-auto text-vibrantorange animate-spin mb-3" strokeWidth={2.5} />
                                <h3 className="font-heading font-black text-2xl text-ink tracking-tighter">Looking for opponent…</h3>
                                <p className="font-body text-sm text-ink/60 mt-1">Ante {ante} cr · pot {ante * 2} cr</p>
                                <button onClick={cancelQueue} className="brutal-btn-white text-sm py-2 px-4 mt-4" data-testid="poker-cancel-queue">
                                    Cancel
                                </button>
                            </div>
                        )}

                        {phase === "playing" && match && (
                            <>
                                <div className="brutal-card p-4 bg-white">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Action timer</span>
                                        <span className="font-heading font-black text-2xl text-ink tabular-nums" data-testid="poker-timer">
                                            {la.actor ? `${Math.ceil(timeLeft)}s` : "—"}
                                        </span>
                                    </div>
                                    <div className="h-2 bg-ink/10 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full ${timeLeft < 5 ? "bg-vibrantpink" : "bg-vibrantorange"}`}
                                            style={{ width: `${la.actor ? Math.max(0, Math.min(100, (timeLeft / (state?.action_clock || 20)) * 100)) : 0}%` }}
                                        />
                                    </div>
                                    <div className="mt-3 grid grid-cols-2 gap-2 text-center text-xs font-heading font-bold text-ink/60">
                                        <div>Pot <div className="text-base text-ink">{state?.pot ?? 0}</div></div>
                                        <div>To call <div className="text-base text-ink">{la.to_call ?? 0}</div></div>
                                    </div>
                                </div>

                                <div className="brutal-card p-4 bg-vibrantyellow space-y-2" data-testid="poker-actions">
                                    {!la.actor && (
                                        <p className="font-heading font-bold text-sm text-ink/70 text-center py-2">Waiting for opponent…</p>
                                    )}
                                    {la.actor && (
                                        <>
                                            <div className="grid grid-cols-2 gap-2">
                                                <button
                                                    onClick={() => sendAction("fold")}
                                                    disabled={!la.can_fold}
                                                    data-testid="poker-act-fold"
                                                    className="brutal-btn bg-vibrantpink text-white py-2 disabled:opacity-40"
                                                >
                                                    Fold
                                                </button>
                                                {la.can_check ? (
                                                    <button
                                                        onClick={() => sendAction("check")}
                                                        data-testid="poker-act-check"
                                                        className="brutal-btn bg-white text-ink py-2"
                                                    >
                                                        Check
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => sendAction("call")}
                                                        disabled={!la.can_call}
                                                        data-testid="poker-act-call"
                                                        className="brutal-btn bg-vibrantblue text-white py-2 disabled:opacity-40"
                                                    >
                                                        Call {la.to_call}
                                                    </button>
                                                )}
                                            </div>
                                            {(la.can_bet || la.can_raise) && la.min_raise_to < la.max_raise_to && (
                                                <div className="bg-white border-[3px] border-ink rounded-xl p-3 shadow-brutal">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">
                                                            {la.can_bet ? "Bet to" : "Raise to"}
                                                        </span>
                                                        <span className="font-heading font-black text-xl text-ink tabular-nums">{raiseTo}</span>
                                                    </div>
                                                    <input
                                                        type="range"
                                                        min={la.min_raise_to}
                                                        max={la.max_raise_to}
                                                        value={raiseTo}
                                                        onChange={(e) => setRaiseTo(parseInt(e.target.value, 10))}
                                                        className="w-full accent-vibrantorange"
                                                        data-testid="poker-raise-slider"
                                                    />
                                                    <div className="flex gap-1 mt-2">
                                                        {[la.min_raise_to, Math.floor((la.min_raise_to + la.max_raise_to) / 2), la.max_raise_to].map((v, i) => (
                                                            <button
                                                                key={i}
                                                                onClick={() => setRaiseTo(v)}
                                                                className="brutal-btn-white py-1 text-[11px] flex-1"
                                                            >
                                                                {i === 0 ? "Min" : i === 2 ? "Max" : v}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-2 gap-2">
                                                {(la.can_bet || la.can_raise) && (
                                                    <button
                                                        onClick={() => sendAction(la.can_bet ? "bet" : "raise", raiseTo)}
                                                        data-testid="poker-act-raise"
                                                        className="brutal-btn bg-vibrantorange text-white py-2"
                                                    >
                                                        {la.can_bet ? "Bet" : "Raise"} {raiseTo}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => sendAction("all_in")}
                                                    disabled={!la.can_all_in}
                                                    data-testid="poker-act-allin"
                                                    className="brutal-btn bg-ink text-white py-2 disabled:opacity-40 col-span-1"
                                                >
                                                    All-in ({la.my_chips})
                                                </button>
                                            </div>
                                        </>
                                    )}
                                    {actionError && (
                                        <p data-testid="poker-action-error" className="font-body font-semibold text-xs text-vibrantpink text-center pt-1">{actionError}</p>
                                    )}
                                </div>

                                <div className="brutal-card p-3 bg-white text-center">
                                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Pool</div>
                                    <div className="font-heading font-black text-2xl text-ink">{match.pool} cr</div>
                                </div>
                            </>
                        )}

                        {phase === "ended" && (
                            <div data-testid="poker-result" className="brutal-card p-6 bg-white text-center">
                                {result ? (
                                    result.winner_id === match?.you?.id ? (
                                        <>
                                            <Trophy className="w-12 h-12 mx-auto text-vibrantorange mb-2" strokeWidth={2.5} />
                                            <h3 className="font-heading font-black text-3xl text-ink tracking-tighter">You win!</h3>
                                            <p className="font-body text-sm text-ink mt-1">+{result.pool} credits</p>
                                        </>
                                    ) : result.winner_id == null ? (
                                        <>
                                            <Coins className="w-12 h-12 mx-auto text-ink mb-2" strokeWidth={2.5} />
                                            <h3 className="font-heading font-black text-2xl text-ink tracking-tighter">Refunded</h3>
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="w-12 h-12 mx-auto text-vibrantpink mb-2" strokeWidth={2.5} />
                                            <h3 className="font-heading font-black text-3xl text-ink tracking-tighter">You lost</h3>
                                            {result.walkover && <p className="font-body text-[11px] text-ink/40 mt-1">(opponent disconnected — pot kept by them)</p>}
                                        </>
                                    )
                                ) : (
                                    <p className="font-body text-ink/60">Disconnected.</p>
                                )}
                                <button
                                    onClick={() => { setPhase("idle"); setResult(null); setMatch(null); setState(null); refresh && refresh(); }}
                                    className="brutal-btn-primary mt-4"
                                    data-testid="poker-play-again"
                                >
                                    Play again
                                </button>
                            </div>
                        )}

                        <PageChat room="poker" title="Players' chat" />
                    </div>
                </div>
            )}
        </div>
    );
}
